import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Modal,
  Segment,
} from 'semantic-ui-react';
import QRCode from 'react-qr-code';
import { API, showError, showInfo, showSuccess } from '../../helpers';
import { renderQuota, renderQuotaNumber } from '../../helpers/render';
import QuotaProgressBar from '../../components/QuotaProgressBar';
import { CONTACT_EMAIL } from '../../constants/common.constant';
import { UserContext } from '../../context/User';
import ConsumptionInvoiceSection from '../../components/ConsumptionInvoiceSection';

const BANK_INFO = {
  accountName: '四川省科智汇川人工智能科技有限责任公司',
  bankName: '中国银行成都自贸试验区分行',
  accountNumber: '119943705160',
};

const TopUp = () => {
  const [userState] = useContext(UserContext);
  const [amount, setAmount] = useState('10');
  const [paymentOrder, setPaymentOrder] = useState(null); // {order_no, code_url, amount_fen}
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle | pending | paid
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isDebug, setIsDebug] = useState(false);
  const [hasPendingHint, setHasPendingHint] = useState(false);
  const [quotaRefreshKey, setQuotaRefreshKey] = useState(0);
  const [quotaForFirstTopup, setQuotaForFirstTopup] = useState(0);
  const [redeemCode, setRedeemCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [tenant, setTenant] = useState(null); // 当前租户站点配置
  const pollTimerRef = useRef(null);
  // 租户站点由 payment_enabled 统一控制微信充值和银行汇款
  const paymentEnabled = !tenant || Boolean(tenant.payment_enabled);
  // 租户用户不支持微信充值，两种充值方式必须保持一致
  const wechatPayEnabled = paymentEnabled && !userState?.user?.tenant;
  const bankTransferEnabled = wechatPayEnabled;
  const topUpColumns = wechatPayEnabled ? 2 : 0;

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const openOrder = (data) => {
    setHasPendingHint(false);
    setPaymentOrder(data);
    setPaymentStatus('pending');
    startPolling(data.order_no);
  };

  const onPaymentSuccess = () => {
    stopPolling();
    setPaymentStatus('paid');
    showSuccess('支付成功！额度已到账');
    setQuotaRefreshKey((k) => k + 1);
    setTimeout(() => {
      setPaymentOrder(null);
      setPaymentStatus('idle');
      setAmount('10');
    }, 2000);
  };

  const createPaymentOrder = async () => {
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum < 0.01 || amountNum > 10000) {
      showInfo('充值金额须在 0.01–10000 元之间');
      return;
    }
    setIsCreatingOrder(true);
    try {
      const res = await API.post('/api/payment/order', {
        amount_fen: Math.round(amountNum * 100),
      });
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      openOrder(data);
    } catch (err) {
      showError('创建订单失败');
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const startPolling = (orderNo) => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const res = await API.get(`/api/payment/order/${orderNo}`);
        const { success, data } = res.data;
        if (!success) return;
        if (data.status === 2) {
          onPaymentSuccess();
        } else if (data.status === 3) {
          stopPolling();
          setPaymentOrder(null);
          setPaymentStatus('idle');
          showError('订单已过期，请重新发起支付');
        }
      } catch (_) {}
    }, 3000);
  };

  const handleDebugPay = async () => {
    if (!paymentOrder) return;
    try {
      const res = await API.post(
        `/api/payment/debug/pay/${paymentOrder.order_no}`
      );
      const { success, message } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      onPaymentSuccess();
    } catch (err) {
      showError('调试支付失败');
    }
  };

  const cancelPayment = () => {
    stopPolling();
    // Final status check — catches the case where the user paid on their phone
    // and dismissed the modal before the poller caught it
    if (paymentOrder) {
      API.get(`/api/payment/order/${paymentOrder.order_no}`)
        .then((res) => {
          const { success, data } = res.data;
          if (success && data.status === 2) {
            setQuotaRefreshKey((k) => k + 1);
            showSuccess('支付成功！额度已到账');
          }
        })
        .catch(() => {});
    }
    setPaymentOrder(null);
    setPaymentStatus('idle');
  };

  useEffect(() => {
    let status = localStorage.getItem('status');
    if (status) {
      status = JSON.parse(status);
      if (status.tenant) setTenant(status.tenant);
      if (status.debug_enabled) setIsDebug(true);
      if (status.quota_for_first_topup > 0)
        setQuotaForFirstTopup(status.quota_for_first_topup);
    }

    API.get('/api/payment/pending')
      .then((res) => {
        const { success, data } = res.data;
        if (success && data) setHasPendingHint(true);
      })
      .catch(() => {});

    return () => stopPolling();
  }, []);

  const handleRedeem = async () => {
    if (!redeemCode.trim()) {
      showInfo('请输入兑换码');
      return;
    }
    setIsRedeeming(true);
    try {
      const res = await API.post('/api/user/topup', { key: redeemCode.trim() });
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(`兑换成功！获得额度 ${renderQuota(data)}`);
        setRedeemCode('');
        setQuotaRefreshKey((k) => k + 1);
      } else {
        showError(message);
      }
    } catch (_) {
      showError('兑换失败');
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleCopyBankInfo = () => {
    const bankInfo = `收款户名：${BANK_INFO.accountName}\n
收款银行：${BANK_INFO.bankName}\n
收款账号：${BANK_INFO.accountNumber}\n
打款备注：${userState.user?.username}`;
    navigator.clipboard
      .writeText(bankInfo)
      .then(() => {
        showSuccess('银行信息已复制到剪贴板');
      })
      .catch(() => {
        showError('复制失败，请手动复制');
      });
  };

  return (
    <>
      <Segment>
        <Header as='h3'>剩余额度</Header>
        <QuotaProgressBar refreshTrigger={quotaRefreshKey} showHeader={false} />
      </Segment>
      {topUpColumns > 0 && (
      <Segment>
        <Header as='h3'>充值额度</Header>
        <Message warning size='small'>
          <Icon name='warning sign' />
          token 包充值后不支持退款！
        </Message>
        <Grid columns={topUpColumns} stackable>
          {wechatPayEnabled && (
          <Grid.Column>
            <Header as='h4'>
              <Icon name='wechat' color='green' />
              微信扫码充值
            </Header>
            {quotaForFirstTopup > 0 && (
              <Message color='teal' size='small'>
                <Icon name='gift' />
                首次充值额外赠送{' '}
                <strong>{renderQuotaNumber(quotaForFirstTopup)}</strong> 额度！
              </Message>
            )}
            {hasPendingHint && (
              <Message warning size='small'>
                系统检测到你有未支付完成的订单，点击下方按钮可继续支付
              </Message>
            )}
            <Form>
              <Form.Field>
                <div style={{ marginBottom: 6 }}>
                  {[10, 100, 1000].map((preset) => (
                    <Label
                      key={preset}
                      as='a'
                      color={amount === String(preset) ? 'blue' : undefined}
                      style={{ marginRight: 6, cursor: 'pointer' }}
                      onClick={() => setAmount(String(preset))}
                    >
                      ¥{preset}
                    </Label>
                  ))}
                  <Label
                    as='a'
                    color={
                      ![10, 100, 1000].includes(Number(amount))
                        ? 'blue'
                        : undefined
                    }
                    style={{ cursor: 'pointer' }}
                    onClick={() => setAmount('')}
                  >
                    其他金额
                  </Label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '1em', color: '#555' }}>¥</span>
                  <Input
                    placeholder='充值金额（元）'
                    type='number'
                    min='0.01'
                    max='10000'
                    step='0.01'
                    value={amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === '-') {
                        setAmount('');
                        return;
                      }
                      const parts = val.split('.');
                      const formatted =
                        parts.length > 1
                          ? parts[0] + '.' + parts[1].slice(0, 2)
                          : val;
                      setAmount(formatted);
                    }}
                  />
                </div>
                {(() => {
                  const quotaPerUnit = parseFloat(
                    localStorage.getItem('quota_per_unit')
                  );
                  const amountNum = parseFloat(amount);
                  if (
                    !quotaPerUnit ||
                    quotaPerUnit <= 0 ||
                    !amountNum ||
                    amountNum <= 0
                  )
                    return null;
                  return (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: '0.85em',
                        color: '#888',
                      }}
                    >
                      约可获得{' '}
                      {renderQuotaNumber(Math.round(amountNum * quotaPerUnit))}{' '}
                      额度
                    </div>
                  );
                })()}
              </Form.Field>
              <Button
                color='green'
                onClick={createPaymentOrder}
                disabled={isCreatingOrder || paymentStatus === 'pending'}
                loading={isCreatingOrder}
              >
                生成支付二维码
              </Button>
            </Form>
          </Grid.Column>
          )}

          {bankTransferEnabled && (
          <Grid.Column>
            <Header as='h4'>
              <Icon name='exchange' color='blue' />
              对公汇款
            </Header>
            <Message info size='small'>
              <Icon name='info circle' />
              对公汇款需 1～3 天到账，账户信息如下：
            </Message>
            <Segment secondary>
              <p style={{ margin: '8px 0', fontSize: '0.95em' }}>
                <strong>收款户名：</strong>
                {BANK_INFO.accountName}
              </p>
              <p style={{ margin: '8px 0', fontSize: '0.95em' }}>
                <strong>收款银行：</strong>
                {BANK_INFO.bankName}
              </p>
              <p style={{ margin: '8px 0', fontSize: '0.95em' }}>
                <strong>收款账号：</strong>
                {BANK_INFO.accountNumber}
              </p>
              <p>
                <Icon name='warning sign' />
                为了能及时准确到账，请打款时备注您的充值账号（
                {userState.user?.username}）
              </p>
              <Button
                color='blue'
                size='small'
                onClick={handleCopyBankInfo}
                style={{ marginTop: '8px' }}
              >
                <Icon name='copy outline' />
                复制银行信息
              </Button>
            </Segment>
          </Grid.Column>
          )}
        </Grid>
        <p style={{ color: '#555', marginTop: '2em' }}>
          <Icon name='mail' />
          如有支付问题，请联系客服：
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </Segment>
      )}

      <Segment>
        <Header as='h3'>兑换码</Header>
        <Form>
          <Form.Field>
            <Input
              placeholder='请输入兑换码'
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRedeem();
              }}
            />
          </Form.Field>
          <Button
            color='yellow'
            onClick={handleRedeem}
            loading={isRedeeming}
            disabled={isRedeeming}
          >
            兑换
          </Button>
        </Form>
      </Segment>

      {/* QR code modal */}
      <Modal open={!!paymentOrder} onClose={cancelPayment} size='small'>
        <Modal.Header>微信扫码支付</Modal.Header>
        <Modal.Content style={{ textAlign: 'center' }}>
          {paymentOrder && paymentStatus === 'pending' && (
            <>
              <p>
                请使用微信扫描下方二维码完成支付，充值金额{' '}
                <strong>¥{(paymentOrder.amount_fen / 100).toFixed(2)}</strong>
              </p>
              <div
                style={{
                  display: 'inline-block',
                  padding: 16,
                  background: '#fff',
                  border: '1px solid #eee',
                  borderRadius: 8,
                }}
              >
                <QRCode value={paymentOrder.code_url} size={200} />
              </div>
              <p style={{ marginTop: 12, color: '#888' }}>等待支付中…</p>
              {isDebug && (
                <Button
                  color='orange'
                  style={{ marginTop: 8 }}
                  onClick={handleDebugPay}
                >
                  调试：模拟支付成功
                </Button>
              )}
            </>
          )}
          {paymentStatus === 'paid' && (
            <p style={{ color: 'green', fontSize: '1.2em' }}>
              支付成功！额度已到账 ✓
            </p>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={cancelPayment}>关闭</Button>
        </Modal.Actions>
      </Modal>

      <ConsumptionInvoiceSection />
    </>
  );
};

export default TopUp;
